/**
 * movementTracker.js — Tracks movement phases and samples landmarks
 * for backend validation.
 *
 * Usage:
 *   const tracker = new MovementTracker(testConfig, side);
 *   // In each frame callback:
 *   tracker.processFrame(landmarks, timestampMs, { faceLandmarks });
 *   // When done:
 *   const { landmarkFrames, localResult } = tracker.getResults();
 */

import { computeAngleForFrame, checkCompensation } from './angleUtils';

const SAMPLE_INTERVAL_MS = 500; // ~2 Hz sampling for backend

export class MovementTracker {
  constructor(testConfig, side = 'left') {
    this.testConfig = testConfig;
    this.side = side;
    this.landmarkConfig = testConfig.landmarkConfig;
    this.movementPhases = testConfig.movementPhases || [];
    this.passCriteria = testConfig.passCriteria || {};
    this.tolerance = testConfig.angleToleranceDegrees || 5;

    // State
    this.currentAngle = 0;
    this.maxAngle = 0;
    this.currentPhaseIdx = 0;
    this.phaseResults = this.movementPhases.map((p) => ({
      name: p.name,
      completed: false,
      enteredAt: null,
      heldSeconds: 0,
    }));
    this.compensationViolations = [];
    this.sampledFrames = [];         // Sampled for backend ({timestamp, landmarks})
    this.computedAngles = [];        // All angles for UI graph
    this.lastSampleTime = 0;
    this.prevTimestamp = null;
    this.isComplete = false;
  }

  /**
   * Process a single frame from MediaPipe.
   * @param {Array} landmarks - Array of 33 landmarks [{x,y,z}, ...]
   * @param {number} timestampMs - frame timestamp in milliseconds
   * @returns {{ angle, phase, violations, allPhasesComplete }}
   */
  processFrame(landmarks, timestampMs, options = {}) {
    if (this.isComplete) {
      return {
        angle: this.currentAngle,
        phase: this._currentPhaseName(),
        violations: [],
        allPhasesComplete: true,
      };
    }

    // Compute primary angle
    const angle = computeAngleForFrame(landmarks, this.landmarkConfig, this.side, {
      faceLandmarks: options.faceLandmarks || null,
    });
    this.currentAngle = angle;
    const absAngle = Math.abs(angle);
    if (absAngle > this.maxAngle) this.maxAngle = absAngle;

    // Record computed angle for UI
    this.computedAngles.push({ timestamp: timestampMs, angle: Math.round(angle * 100) / 100 });

    // Check compensations
    const compChecks = this.landmarkConfig.compensation_checks || [];
    const violations = checkCompensation(landmarks, compChecks, this.side);
    if (violations.length > 0) {
      this.compensationViolations.push(
        ...violations.map((v) => ({ ...v, timestamp: timestampMs }))
      );
    }

    // Sample for backend at ~2Hz
    if (timestampMs - this.lastSampleTime >= SAMPLE_INTERVAL_MS) {
      // DEBUG: log frontend angle at sample time — match against backend [CV_DEBUG][BACKEND] by timestamp
      console.log(
        `[CV_DEBUG][FRONTEND] t=${timestampMs}ms | calc=${this.landmarkConfig.calculation_type || 'standard'} | side=${this.side} | angle=${Math.round(angle * 100) / 100}°`
        + ` | raw_angle=${angle}`
      );
      this.sampledFrames.push({
        timestamp: timestampMs,
        landmarks: landmarks.map((lm) => ({ x: lm.x, y: lm.y, z: lm.z || 0 })),
        ...(Array.isArray(options.faceLandmarks) && options.faceLandmarks.length > 0
          ? {
              face_landmarks: options.faceLandmarks.map((lm) => ({
                x: lm.x || 0,
                y: lm.y || 0,
                z: lm.z || 0,
              })),
            }
          : {}),
      });
      this.lastSampleTime = timestampMs;
    }

    // Phase tracking
    this._updatePhase(angle, timestampMs);

    const allPhasesComplete = this.phaseResults.every((p) => p.completed);
    if (allPhasesComplete) {
      this.isComplete = true;
    }

    this.prevTimestamp = timestampMs;

    return {
      angle: Math.round(angle * 100) / 100,
      phase: this._currentPhaseName(),
      violations,
      allPhasesComplete,
    };
  }

  _currentPhaseName() {
    if (this.currentPhaseIdx < this.movementPhases.length) {
      return this.movementPhases[this.currentPhaseIdx].name;
    }
    return 'complete';
  }

  _angleInRange(angle, range) {
    const lo = Math.min(...range);
    const hi = Math.max(...range);
    return Math.abs(angle) >= lo - this.tolerance && Math.abs(angle) <= hi + this.tolerance;
  }

  _updatePhase(angle, timestampMs) {
    if (this.currentPhaseIdx >= this.movementPhases.length) return;

    const phase = this.movementPhases[this.currentPhaseIdx];
    const pr = this.phaseResults[this.currentPhaseIdx];
    const range = phase.angle_range || [0, 180];
    const direction = phase.direction || null;
    const absAngle = Math.abs(angle);
    const inRange = this._angleInRange(angle, range);

    // Match backend behavior: for directional phases, do not hard-cap by upper range.
    // This lets "increasing" movement continue and still complete once threshold is reached.
    let validForPhase = inRange;
    if (!validForPhase) {
      const startThreshold = Array.isArray(range) && range.length > 0 ? Math.abs(range[0]) : 0;
      if (direction === 'increasing' && absAngle >= startThreshold - this.tolerance) {
        validForPhase = true;
      } else if (direction === 'decreasing' && absAngle <= startThreshold + this.tolerance) {
        validForPhase = true;
      }
    }

    if (validForPhase) {
      if (pr.enteredAt === null) {
        pr.enteredAt = timestampMs;
      }
      // Accumulate hold time
      if (this.prevTimestamp !== null) {
        pr.heldSeconds += (timestampMs - this.prevTimestamp) / 1000;
      }
      const holdRequired = phase.hold_seconds || 0;
      if (holdRequired <= 0 || pr.heldSeconds >= holdRequired) {
        pr.completed = true;
        this.currentPhaseIdx++;
      }
    }
  }

  /**
   * Get results for sending to backend and local display.
   */
  getResults() {
    const minAngleReq = this.passCriteria.min_angle_reached || 0;
    const maxCompViolations = this.passCriteria.max_compensation_violations ?? Infinity;
    const allPhasesComplete = this.phaseResults.every((p) => p.completed);

    let testPassed = true;
    if (this.maxAngle < minAngleReq) testPassed = false;
    if (this.passCriteria.movement_completed && !allPhasesComplete) testPassed = false;
    if (this.compensationViolations.length > maxCompViolations) testPassed = false;

    return {
      landmarkFrames: this.sampledFrames,
      localResult: {
        computedAngles: this.computedAngles,
        phaseResults: this.phaseResults,
        maxAngleReached: Math.round(this.maxAngle * 100) / 100,
        compensationViolations: this.compensationViolations,
        testPassed,
      },
    };
  }

  /**
   * Return sampled landmark frames (for saving to backend).
   */
  getSampledFrames() {
    return this.sampledFrames;
  }

  /**
   * Reset tracker for retry.
   */
  reset() {
    this.currentAngle = 0;
    this.maxAngle = 0;
    this.currentPhaseIdx = 0;
    this.phaseResults = this.movementPhases.map((p) => ({
      name: p.name,
      completed: false,
      enteredAt: null,
      heldSeconds: 0,
    }));
    this.compensationViolations = [];
    this.sampledFrames = [];
    this.computedAngles = [];
    this.lastSampleTime = 0;
    this.prevTimestamp = null;
    this.isComplete = false;
  }
}
